<!-- AgentDashboard.vue -->
<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import AdminLayout from '@/components/layout/AdminLayout.vue'
import type { Tender } from '@/types/tender'
import HeadingDefault from '@/components/Heading/HeadingOne.vue'
import {
  CircleQuestionIcon,
  DoNotEnterIcon,
  ClockIcon,
  EyeIcon,
  FileExportIcon,
  FileSearchIcon,
  IndianRupeeIcon,
  SigmaIcon,
  BanIcon,
  RightLeftLargeIcon,
  InProgressIcon,
  SquareCheckIcon,
  WonIcon,
  FileCheckIcon
} from '@/icons'
import api from '@/services/api'
import { getAxiosErrorMessage } from '@/utils/errorHandler'
import StatsCards from '@/components/cards/StatsCards.vue'

// Define proper interface for tender with workflowStatus
interface TenderWithWorkflow extends Tender {
  workflowStatus?:
    | 'assigned-to-agent'
    | 'in-progress'
    | 'price-review'
    | 'criteria-review'
    | 'ready-to-bid'
    | 'not-fit'
    | 'not-bidded'
    | 'submitted'
    | 'under-query'
    | 'won'
    | 'lost'
    | 'completed'
  assignedAgentName?: string
  agentNotes?: string
  agentAssignedAt?: string
}

const tenders = ref<TenderWithWorkflow[]>([])
const loading = ref(false)
const error = ref<string | null>(null)

// Statistics - only check workflowStatus
const totalTenderCount = computed(() => tenders.value.length)
const assignedCount = computed(
  () => tenders.value.filter((t) => t.workflowStatus === 'assigned-to-agent').length,
)
const inProgressCount = computed(
  () => tenders.value.filter((t) => t.workflowStatus === 'in-progress').length,
)
const priceReviewCount = computed(
  () => tenders.value.filter((t) => t.workflowStatus === 'price-review').length,
)
const criteriaReviewCount = computed(
  () => tenders.value.filter((t) => t.workflowStatus === 'criteria-review').length,
)
const readyToBidCount = computed(
  () => tenders.value.filter((t) => t.workflowStatus === 'ready-to-bid').length,
)
const notFitCount = computed(
  () => tenders.value.filter((t) => t.workflowStatus === 'not-fit').length,
)
const notBiddedCount = computed(
  () => tenders.value.filter((t) => t.workflowStatus === 'not-bidded').length,
)
const submittedCount = computed(
  () => tenders.value.filter((t) => t.workflowStatus === 'submitted').length,
)
const underQueryCount = computed(
  () => tenders.value.filter((t) => t.workflowStatus === 'under-query').length,
)
const wonCount = computed(
  () => tenders.value.filter((t) => t.workflowStatus === 'won').length,
)
const lostCount = computed(
  () => tenders.value.filter((t) => t.workflowStatus === 'lost').length,
)
const completedCount = computed(
  () => tenders.value.filter((t) => t.workflowStatus === 'completed').length,
)

// Calculate upcoming and expired deadlines
const upcomingDeadlinesCount = computed(() => {
  const now = new Date()
  const sevenDaysFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000)

  return tenders.value.filter((tender) => {
    if (!tender.deadline) return false
    const deadline = new Date(tender.deadline)
    return deadline > now && deadline <= sevenDaysFromNow
  }).length
})

const expiredDeadlinesCount = computed(() => {
  const now = new Date()

  return tenders.value.filter((tender) => {
    if (!tender.deadline) return false
    const deadline = new Date(tender.deadline)
    return deadline < now
  }).length
})

// Fetch assigned tenders for current agent
const fetchMyTenders = async () => {
  try {
    loading.value = true
    error.value = null
    console.log('🔄 Fetching my assigned tenders...')

    const response = await api.get('/api/tenders/my-agent-tenders')

    console.log('📊 My tenders response:', response.data)

    if (response.data.success) {
      tenders.value = response.data.tenders || []
      console.log(`✅ Found ${tenders.value.length} tenders assigned to me`)
    } else {
      throw new Error(response.data.error || 'Failed to fetch your tenders')
    }
  } catch (err) {
    console.error('❌ Error fetching my tenders:', err)
    error.value = getAxiosErrorMessage(err)
  } finally {
    loading.value = false
  }
}

// Lifecycle
onMounted(() => {
  fetchMyTenders()
})
</script>

<template>
  <AdminLayout>
    <HeadingDefault heading="Dashboard" />
    <div class="space-y-8">

      <section>
        <div class="flex items-center justify-between mb-3">
          <h2 class="text-base font-semibold text-gray-700 dark:text-gray-200">Tender Overview</h2>
          <!-- <router-link to="/tenders" class="text-xs text-blue-600 hover:underline font-medium">
            View &amp; Manage →
          </router-link> -->
        </div>
        <div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          <StatsCards title="Total Tender" :value="totalTenderCount" :icon="SigmaIcon" />

          <router-link to="/assigned-tenders-agent" class="group block">
            <StatsCards title="Assigned Tender" :value="assignedCount" :icon="SquareCheckIcon" class="group-hover:ring-2 group-hover:ring-blue-400 transition rounded-lg" />
          </router-link>

          <router-link to="/in-progress-agent" class="group block">
            <StatsCards title="In Progress" :value="inProgressCount" :icon="InProgressIcon" class="group-hover:ring-2 group-hover:ring-green-400 transition rounded-lg" />
          </router-link>

          <router-link to="/price-review-agent" class="group block">
            <StatsCards title="Price Review" :value="priceReviewCount" :icon="IndianRupeeIcon" class="group-hover:ring-2 group-hover:ring-blue-400 transition rounded-lg" />
          </router-link>

          <router-link to="/criteria-review-agent" class="group block">
            <StatsCards title="Criteria Review" :value="criteriaReviewCount" :icon="FileSearchIcon" class="group-hover:ring-2 group-hover:ring-blue-400 transition rounded-lg" />
          </router-link>

          <router-link to="/ready-to-bid-agent" class="group block">
            <StatsCards title="Ready To Bid" :value="readyToBidCount" :icon="RightLeftLargeIcon" class="group-hover:ring-2 group-hover:ring-green-400 transition rounded-lg" />
          </router-link>

          <router-link to="/not-fit-agent" class="group block">
            <StatsCards title="Not Fit" :value="notFitCount" :icon="DoNotEnterIcon" class="group-hover:ring-2 group-hover:ring-red-400 transition rounded-lg" />
          </router-link>

          <router-link to="/not-bidded-agent" class="group block">
            <StatsCards title="Not Bidded" :value="notBiddedCount" :icon="ClockIcon" class="group-hover:ring-2 group-hover:ring-red-400 transition rounded-lg" />
          </router-link>

          <router-link to="/submitted-tender-agent" class="group block">
            <StatsCards title="Submitted" :value="submittedCount" :icon="FileExportIcon" class="group-hover:ring-2 group-hover:ring-green-400 transition rounded-lg" />
          </router-link>


          <router-link to="/under-query-agent" class="group block">
            <StatsCards
              title="Under Query"
              :value="underQueryCount"
              :icon="CircleQuestionIcon"
              class="group-hover:ring-2 group-hover:ring-blue-400 transition rounded-lg"
            />
          </router-link>

          <router-link to="/won-tender-agent" class="group block">
            <StatsCards
              title="Won Tenders"
              :value="wonCount"
              :icon="WonIcon"
              class="group-hover:ring-2 group-hover:ring-green-400 transition rounded-lg"
            />
          </router-link>

          <router-link to="/lost-tender-agent" class="group block">
            <StatsCards
              title="Lost Tenders"
              :value="lostCount"
              :icon="BanIcon"
              class="group-hover:ring-2 group-hover:ring-red-400 transition rounded-lg"
            />
          </router-link>

          <router-link to="/completed-tender-agent" class="group block">
            <StatsCards
              title="Completed Tenders"
              :value="completedCount"
              :icon="FileCheckIcon"
              class="group-hover:ring-2 group-hover:ring-red-400 transition rounded-lg"
            />
          </router-link>


        </div>
      </section>

      <section>
        <div class="flex items-center justify-between mb-3">
          <h2 class="text-base font-semibold text-gray-700 dark:text-gray-200">Red Alert</h2>
          <!-- <router-link to="/tenders" class="text-xs text-blue-600 hover:underline font-medium">
            View &amp; Manage →
          </router-link> -->
        </div>
        <div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          <StatsCards title="Upcoming Deadlines" :value="upcomingDeadlinesCount" :icon="EyeIcon" />
          <StatsCards title="Expired" :value="expiredDeadlinesCount" :icon="EyeIcon" />
        </div>
      </section>
    </div>
  </AdminLayout>
</template>
